import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";
import "../../../../assets/font/THSarabunNew-normal";
import "../../../../assets/font/THSarabunNew-bold";

// ════════════════════════════════════════════════════════════
//  Constants
// ════════════════════════════════════════════════════════════

const STATUS_ID_MAP = {
  1: "ส่งฟ้อง",
  2: "เตรียมเอกสาร",
  3: "เตรียมเอกสารสำเร็จ",
  4: "รอเลขคดีดำ",
  5: "รอศาลพิพากษา",
  6: "เอกสารไม่ครบ",
  7: "สร้างคำฟ้อง",
  8: "ถอนฟ้อง",
  9: "พิพากษา",
  10: "พิพากษาทำยอม",
  11: "วางคำบังคับ",
  12: "คดีถึงที่สุด",
  13: "อุทธรณ์",
  14: "ปิดบัญชี",
  15: "ตรวจคำพิพากษา",
  16: "ตรวจคำพิพากษาสำเร็จ",
  17: "รอ ceo อนุมัติ",
  18: "ceo อนุมัติ",
  19: "รอบัญชีตรวจ",
  20: "บัญชีตรวจสำเร็จ",
  21: "บัญชีตีกลับ",
};

const SUCCESS_STATUS_THRESHOLD = 13;
const ENFORCE_RECEIVED_STATUS = "รับงานบังคับคดี";

const STATUS_ORDER_MAP = {
  ส่งฟ้อง: 1,
  เตรียมเอกสาร: 2,
  เตรียมเอกสารสำเร็จ: 3,
  เอกสารไม่ครบ: 3.1,
  สร้างคำฟ้อง: 4,
  ถอนฟ้อง: 4.1,
  อุทธรณ์: 4.2,
  รอเลขคดีดำ: 5,
  รอศาลพิพากษา: 6,
  พิพากษา: 7,
  พิพากษาทำยอม: 7.1,
  วางคำบังคับ: 7.2,
  คดีถึงที่สุด: 7.3,
  ตรวจคำพิพากษา: 8,
  ตรวจคำพิพากษาสำเร็จ: 8.1,
  บัญชีตีกลับ: 8.2,
  รอบัญชีตรวจ: 9,
  บัญชีตรวจสำเร็จ: 9.1,
  "รอ ceo อนุมัติ": 10,
  "ceo อนุมัติ": 10.1,
  รับงานบังคับคดี: 10.2,
  "ปิดบัญชี (สัญญาใหม่)": 11,
  ปิดบัญชี: 11.1,
};

const GROUPS = {
  prep: (s) =>
    s.includes("สืบทรัพย์") ||
    s.includes("บอกเลิก") ||
    s === "รอดำเนินการ" ||
    s === "งานธุรการ",
  prepLawsuit: (s) =>
    s.includes("เตรียมเอกสาร") ||
    s.includes("สร้างคำฟ้อง") ||
    s.includes("ส่งฟ้อง") ||
    s.includes("เอกสารไม่ครบ"),
  court: (s) =>
    s === "ถอนฟ้อง" ||
    s === "อุทธรณ์" ||
    s === "รอเลขคดีดำ" ||
    s === "รอศาลพิพากษา" ||
    s === "พิพากษา" ||
    s === "พิพากษาทำยอม" ||
    s === "วางคำบังคับ" ||
    s === "คดีถึงที่สุด",
  review: (s) =>
    s.includes("ceo") ||
    s.includes("บัญชี") ||
    s.includes("ตรวจ") ||
    s.includes("สัญญาใหม่") ||
    s.includes("ปิดบัญชี") ||
    s === "รับงานบังคับคดี",
};

const getGroupWeight = (status) => {
  if (GROUPS.prep(status)) return 1;
  if (GROUPS.prepLawsuit(status)) return 2;
  if (GROUPS.court(status)) return 3;
  if (GROUPS.review(status)) return 4;
  return 5;
};

const getStatusWeight = (item) => {
  const id = parseInt(item.currentStatus, 10);
  const statusName = item.enforceReceiveDate
    ? ENFORCE_RECEIVED_STATUS
    : (STATUS_ID_MAP[id] ?? item.NAME_MAIN_STATUS ?? "-");
  const orderWeight = STATUS_ORDER_MAP[statusName];
  if (orderWeight !== undefined) return orderWeight;
  return getGroupWeight(statusName) * 100;
};

// ════════════════════════════════════════════════════════════
//  Helpers
// ════════════════════════════════════════════════════════════

const resolveStatus = (item, getTaskStatus) => {
  // รับงานบังคับคดี แสดงแทน "สำเร็จ" (ยกเว้นปิดบัญชีแล้ว ตาม getTaskStatus)
  if (item.enforceReceiveDate) {
    const taskStatus = getTaskStatus
      ? getTaskStatus(item)
      : ENFORCE_RECEIVED_STATUS;
    if (taskStatus === ENFORCE_RECEIVED_STATUS) return taskStatus;
  }
  const st = parseInt(item.currentStatus, 10);
  if (!isNaN(st) && st >= SUCCESS_STATUS_THRESHOLD) return "สำเร็จ";
  return getTaskStatus ? getTaskStatus(item) : (item.NAME_MAIN_STATUS ?? "-");
};

// ✅ แก้ hasAsset — ตรวจสอบหลายเงื่อนไข
const resolveHasAsset = (item) => {
  const statusNum = parseInt(item.statusActiveInvestigate, 10);
  const hasStatus = !isNaN(statusNum) && statusNum === 3;
  const hasList = Array.isArray(item.ASSET_LIST) && item.ASSET_LIST.length > 0;
  return hasStatus || hasList ? "✓" : "✗";
};

const getExpenseBreakdown = (item) => {
  if (!Array.isArray(item.EXPENSE_LIST)) return { items: {}, total: 0 };
  const items = {};
  let total = 0;
  for (const exp of item.EXPENSE_LIST) {
    const key = exp.expenseDescription || "ค่าใช้จ่ายอื่น";
    const amount = parseFloat(exp.PAY ?? exp.NETPAY) || 0;
    items[key] = (items[key] || 0) + amount;
    total += amount;
  }
  return { items, total };
};

const fmt = (n) =>
  !n || n === 0
    ? "-"
    : Number(n).toLocaleString("th-TH", { minimumFractionDigits: 2 });

const collectAllExpenseKeys = (data) => {
  const keys = new Set();
  for (const item of data)
    for (const exp of item.EXPENSE_LIST ?? [])
      keys.add(exp.expenseDescription || "ค่าใช้จ่ายอื่น");
  return [...keys];
};

const hasPaymentList = (data) =>
  data.some((item) => Array.isArray(item.PAYMENT_LIST));

// รวมยอดรับชำระของแต่ละสัญญา + งวด/วันที่จ่ายล่าสุด
const getPaymentSummary = (item) => {
  const list = Array.isArray(item.PAYMENT_LIST) ? item.PAYMENT_LIST : [];
  const sumOf = (key) =>
    list.reduce((s, p) => s + (parseFloat(p[key]) || 0), 0);
  const latest = list.reduce((last, p) => {
    if (!last) return p;
    const diff = dayjs(p.PAYDT).diff(dayjs(last.PAYDT));
    return diff > 0 || (diff === 0 && Number(p.NOPAY) > Number(last.NOPAY))
      ? p
      : last;
  }, null);
  return {
    principal: sumOf("PAYAMT_N"),
    vat: sumOf("PAYAMT_V"),
    interest: sumOf("INTAMT"),
    discount: sumOf("DISCT"),
    lastNopay: latest?.NOPAY ?? "-",
    lastPayDate: latest?.PAYDT ? dayjs(latest.PAYDT).format("DD MMM YY") : "-",
  };
};

const hasEnforceData = (data) =>
  data.some((item) => item.enforceReceiveDate !== undefined);

const getCurrencyColNames = (allExpenseKeys) =>
  new Set([
    "ทุนทรัพย์ที่ฟ้อง (บาท)",
    "รับชำระรวม (บาท)",
    "เงินต้นรวม (บาท)",
    "VAT รวม (บาท)",
    "ดอกเบี้ยรวม (บาท)",
    "ส่วนลดรวม (บาท)",
    "รวมค่าใช้จ่ายในคดี (บาท)",
    "ลูกหนี้คงเหลือ (บาท)",
    "รวมคำพิพากษา (บาท)",
    "ส่วนต่างขาดทุน (บาท)",
    ...allExpenseKeys.map((k) => `${k} (บาท)`),
  ]);

const buildStatusCount = (exportData) => {
  const map = {};
  for (const row of exportData) {
    const st = String(row["สถานะ"] ?? "-");
    map[st] = (map[st] || 0) + 1;
  }
  return Object.entries(map).sort((a, b) => {
    const ga = getGroupWeight(a[0]),
      gb = getGroupWeight(b[0]);
    if (ga !== gb) return ga - gb;
    const wa = STATUS_ORDER_MAP[a[0]] ?? 999;
    const wb = STATUS_ORDER_MAP[b[0]] ?? 999;
    return wa - wb;
  });
};

// ════════════════════════════════════════════════════════════
//  Sort
// ════════════════════════════════════════════════════════════

// เลขสัญญา เช่น "8-1234" กับ "V8-1234" (สัญญารีไฟแนนซ์) ต้องเรียงติดกัน
// ใช้เลขนำหน้า (ไม่สน "V") เทียบแบบตัวเลขจริง ๆ ไม่ใช่ตัวอักษร (กัน "22" มาก่อน "3")
const parseContnoKey = (contno) => {
  const str = String(contno ?? "").trim();
  const hasV = /^v/i.test(str);
  const rest = hasV ? str.slice(1) : str;
  const match = rest.match(/^(\d+)/);
  const num = match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
  const tail = match ? rest.slice(match[0].length) : rest;
  return { num, hasV, tail };
};

const compareContno = (contnoA, contnoB) => {
  const a = parseContnoKey(contnoA);
  const b = parseContnoKey(contnoB);
  if (a.num !== b.num) return a.num - b.num;
  if (a.hasV !== b.hasV) return a.hasV ? 1 : -1;
  return a.tail.localeCompare(b.tail, "th", { numeric: true });
};

// sortBy: "contno" = เลขสัญญา → สถานะ, "status" = สถานะ → เลขสัญญา
export const sortData = (data, sortBy = "contno") =>
  [...data].sort((a, b) => {
    const cmpContno = compareContno(a.CONTNO, b.CONTNO);
    const wA = getStatusWeight(a),
      wB = getStatusWeight(b);
    if (sortBy === "status") {
      if (wA !== wB) return wA - wB;
      if (cmpContno !== 0) return cmpContno;
    } else {
      if (cmpContno !== 0) return cmpContno;
      if (wA !== wB) return wA - wB;
    }
    const da = a.considerationDate
      ? dayjs(a.considerationDate).valueOf()
      : Infinity;
    const db = b.considerationDate
      ? dayjs(b.considerationDate).valueOf()
      : Infinity;
    return da - db;
  });

// ════════════════════════════════════════════════════════════
//  Prepare Data  (shared)
// ════════════════════════════════════════════════════════════

const prepareDataForExport = (data, getTaskStatus) => {
  const allExpenseKeys = collectAllExpenseKeys(data);
  const showPayment = hasPaymentList(data);
  const showEnforce = hasEnforceData(data);
  return data.map((item, index) => {
    const fullName =
      `${item.SNAM ?? ""}${item.NAME1 ?? ""} ${item.NAME2 ?? ""}`.trim();
    const status = resolveStatus(item, getTaskStatus);
    const capital = parseFloat(item.litigationFunds) || 0;
    const totalJudge = parseFloat(item.totalJugde) || 0;
    const loan = item.db2?.LOAN || {};
    const isRps = loan.DATA_TYPE === "rpsl" || loan.DATA_TYPE === "rpsl-h";
    const balance =
      parseFloat(
        isRps ? loan.NCSHPRC - loan.SMPAY : loan.TOTPRC - loan.SMPAY,
      ) || 0;
    const { items: expItems, total: totalExpense } = getExpenseBreakdown(item);

    const expenseCols = Object.fromEntries(
      allExpenseKeys.map((k) => [`${k} (บาท)`, expItems[k] ?? 0]),
    );

    return {
      ลำดับ: index + 1,
      เลขสัญญา: item.CONTNO || "-",
      เลขสัญญาใหม่: item.newContno || "-",
      "ชื่อ-นามสกุล": fullName,
      สถานะ: status,
      ทนาย: item.NNAME || "-",
      เลขคดีดำ: item.blackCaseNumber || "-",
      วันนัดศาล: item.considerationDate
        ? dayjs(item.considerationDate).format("DD MMM YY")
        : "-",
      ศาล: item.provincialCourt || "-",
      เลขคดีแดง: item.redCaseNumber || "-",
      มีทรัพย์:
        item.statusActiveInvestigate === 3
          ? "มี"
          : item.statusActiveInvestigate === 4
            ? "ไม่มี"
            : "-",
      "ทุนทรัพย์ที่ฟ้อง (บาท)": capital,
      ...expenseCols,
      "รวมค่าใช้จ่ายในคดี (บาท)": totalExpense,
      "ลูกหนี้คงเหลือ (บาท)": balance,
      "รวมคำพิพากษา (บาท)": totalJudge,
      "ส่วนต่างขาดทุน (บาท)": balance - totalJudge,
      ...(showEnforce && {
        ผู้รับงานบังคับคดี: item.enforceReceiveByName || "-",
        วันที่รับงานบังคับคดี: item.enforceReceiveDate
          ? dayjs(item.enforceReceiveDate).format("DD MMM YY")
          : "-",
      }),
      ...(showPayment &&
        (() => {
          const pay = getPaymentSummary(item);
          return {
            "รับชำระรวม (บาท)": parseFloat(item.TOTAL_PAY) || 0,
            "เงินต้นรวม (บาท)": pay.principal,
            "VAT รวม (บาท)": pay.vat,
            "ดอกเบี้ยรวม (บาท)": pay.interest,
            "ส่วนลดรวม (บาท)": pay.discount,
            งวดที่จ่ายล่าสุด: pay.lastNopay,
            วันที่จ่ายล่าสุด: pay.lastPayDate,
          };
        })()),
    };
  });
};

const prepareDataForExportMeeting = (data, getTaskStatus) => {
  return data.map((item, index) => {
    const fullName =
      `${item.SNAM ?? ""}${item.NAME1 ?? ""} ${item.NAME2 ?? ""}`.trim();
    const status = resolveStatus(item, getTaskStatus);

    return {
      ลำดับ: index + 1,
      เลขสัญญา: item.CONTNO || "-",
      "ชื่อ-นามสกุล": fullName,
      สถานะ: status,
      เลขคดีดำ: item.blackCaseNumber || "-",
      วันนัดศาล: item.considerationDate
        ? dayjs(item.considerationDate).format("DD MMM YY")
        : "-",
      ศาล: item.provincialCourt || "-",
      มีทรัพย์:
        item.statusActiveInvestigate === 3
          ? "มี"
          : item.statusActiveInvestigate === 4
            ? "ไม่มี"
            : item.statusActiveInvestigate === 2
              ? "เบลส่งสืบ"
              : "-",
    };
  });
};

// ════════════════════════════════════════════════════════════
//  Excel style helpers
// ════════════════════════════════════════════════════════════

const BORDER_THIN = (rgb = "DDDDDD") => ({
  top: { style: "thin", color: { rgb } },
  bottom: { style: "thin", color: { rgb } },
  left: { style: "thin", color: { rgb } },
  right: { style: "thin", color: { rgb } },
});

const XL_HEADER = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
  fill: { patternType: "solid", fgColor: { rgb: "1F61D1" } },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  border: BORDER_THIN("AAAAAA"),
};
const XL_FOOTER = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
  fill: { patternType: "solid", fgColor: { rgb: "2C3E50" } },
  alignment: { horizontal: "right", vertical: "center" },
  border: BORDER_THIN("2C3E50"),
};
const XL_SUMMARY_HEADER = {
  font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
  fill: { patternType: "solid", fgColor: { rgb: "1F61D1" } },
  alignment: { horizontal: "center", vertical: "center" },
  border: BORDER_THIN("1F61D1"),
};

const MONEY_FMT = "#,##0.00";
const INT_FMT = "0";

// ════════════════════════════════════════════════════════════
//  Sheet รายการรับชำระ (PAYMENT_LIST)
// ════════════════════════════════════════════════════════════

const PAYMENT_COLUMNS = [
  { title: "ลำดับ", type: "int" },
  { title: "เลขสัญญา" },
  { title: "เลขสัญญาใหม่" },
  { title: "ชื่อผู้ชำระ" },
  { title: "ทนาย" },
  { title: "เลขที่ใบเสร็จ" },
  { title: "วันที่ใบเสร็จ" },
  { title: "วันที่ชำระ" },
  { title: "งวดที่", type: "int" },
  { title: "รายการ" },
  { title: "ยอดชำระ (บาท)", type: "money" },
  { title: "เงินต้น (บาท)", type: "money" },
  { title: "VAT (บาท)", type: "money" },
  { title: "ดอกเบี้ย (บาท)", type: "money" },
  { title: "ส่วนลด (บาท)", type: "money" },
  { title: "ยอดสุทธิ (บาท)", type: "money" },
];

const preparePaymentRows = (data) => {
  const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YY") : "-");
  const rows = [];
  data.forEach((item) => {
    const defMap = new Map((item.judgeDef ?? []).map((d) => [d.idJudgeDef, d]));
    (item.PAYMENT_LIST ?? []).forEach((pay) => {
      const payer = defMap.get(pay.idJudgeDef) || item;
      rows.push([
        rows.length + 1,
        item.CONTNO || "-",
        pay.CONTNO || item.newContno || "-",
        `${payer.SNAM ?? ""}${payer.NAME1 ?? ""} ${payer.NAME2 ?? ""}`.trim(),
        item.NNAME || "-",
        pay.TMBILL || "-",
        fmtDate(pay.TMBILDT),
        fmtDate(pay.PAYDT),
        Number(pay.NOPAY) || 0,
        pay.FORDESC || "-",
        parseFloat(pay.PAYAMT) || 0,
        parseFloat(pay.PAYAMT_N) || 0,
        parseFloat(pay.PAYAMT_V) || 0,
        parseFloat(pay.INTAMT) || 0,
        parseFloat(pay.DISCT) || 0,
        parseFloat(pay.NETPAY) || 0,
      ]);
    });
  });
  return rows;
};

const buildPaymentSheet = (data) => {
  const rows = preparePaymentRows(data);
  const footer = PAYMENT_COLUMNS.map((col, C) => {
    if (C === 0) return "รวม";
    if (C === 3) return `${rows.length} รายการ`;
    if (col.type === "money")
      return rows.reduce((s, r) => s + (Number(r[C]) || 0), 0);
    return "";
  });

  const ws = XLSX.utils.aoa_to_sheet([
    PAYMENT_COLUMNS.map((c) => c.title),
    ...rows,
    footer,
  ]);
  ws["!cols"] = PAYMENT_COLUMNS.map((col, C) => ({
    wch: Math.max(
      col.title.length + 2,
      ...rows.map((r) => String(r[C] ?? "").length + 1),
      col.type === "money" ? 14 : 0,
    ),
  }));
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  PAYMENT_COLUMNS.forEach((col, C) => {
    const colLetter = XLSX.utils.encode_col(C);
    const isMoney = col.type === "money";
    const isInt = col.type === "int";

    ws[`${colLetter}1`].s = XL_HEADER;

    for (let R = 1; R <= rows.length; R++) {
      const ref = `${colLetter}${R + 1}`;
      if (!ws[ref]) continue;
      ws[ref].s = {
        font: { sz: 11, color: { rgb: "1A1A1A" } },
        fill: {
          patternType: "solid",
          fgColor: { rgb: R % 2 === 0 ? "EBF5FB" : "FFFFFF" },
        },
        alignment: {
          horizontal: isMoney ? "right" : isInt ? "center" : "left",
          vertical: "center",
        },
        numFmt: isMoney ? MONEY_FMT : isInt ? INT_FMT : "@",
        border: BORDER_THIN(),
      };
      if (isMoney) ws[ref].z = MONEY_FMT;
    }

    const fRef = `${colLetter}${rows.length + 2}`;
    if (!ws[fRef]) ws[fRef] = { t: "s", v: "" };
    ws[fRef].s = {
      ...XL_FOOTER,
      alignment: {
        horizontal: isMoney ? "right" : "left",
        vertical: "center",
      },
    };
    if (isMoney) ws[fRef].z = MONEY_FMT;
  });

  return ws;
};

// ════════════════════════════════════════════════════════════
//  Preview (ข้อมูลชุดเดียวกับ Excel)
// ════════════════════════════════════════════════════════════

export const buildExcelPreview = (data, getTaskStatus) => {
  const reportRows = prepareDataForExport(data, getTaskStatus);
  return {
    reportRows,
    reportColumns: Object.keys(reportRows[0] || {}),
    currencyNames: getCurrencyColNames(collectAllExpenseKeys(data)),
    statusCounts: buildStatusCount(reportRows),
    showPayment: hasPaymentList(data),
    paymentColumns: PAYMENT_COLUMNS,
    paymentRows: preparePaymentRows(data),
  };
};

// ════════════════════════════════════════════════════════════
//  Export Excel
// ════════════════════════════════════════════════════════════

export const exportToExcel = (
  data,
  getTaskStatus,
  fileName = "รายงานสรุปงานทนาย.xlsx",
) => {
  const sortedData = data; // เรียงมาแล้วจากหน้า dashboard (sortData)
  const exportData = prepareDataForExport(sortedData, getTaskStatus);
  const allExpenseKeys = collectAllExpenseKeys(sortedData);
  const currencyNames = getCurrencyColNames(allExpenseKeys);
  const statusCounts = buildStatusCount(exportData);
  const tableColumns = Object.keys(exportData[0] || {});

  // ── Summary values ─────────────────────────────────────────
  const totalJudgmentAll = sortedData.reduce(
    (s, i) => s + (parseFloat(i.totalJugde) || 0),
    0,
  );
  const totalBalanceAll = exportData.reduce(
    (s, r) => s + (Number(r["ลูกหนี้คงเหลือ (บาท)"]) || 0),
    0,
  );
  const totalDiffAll = exportData.reduce(
    (s, r) => s + (Number(r["ส่วนต่างขาดทุน (บาท)"]) || 0),
    0,
  );
  const totalExpenseAll = sortedData.reduce(
    (s, i) => s + getExpenseBreakdown(i).total,
    0,
  );
  const successCount = sortedData.filter(
    (i) => parseInt(i.currentStatus, 10) >= SUCCESS_STATUS_THRESHOLD,
  ).length;

  // ── Rows & footer ──────────────────────────────────────────
  const sheetRows = exportData.map((row) =>
    tableColumns.map((col) => row[col]),
  );
  const footerValues = tableColumns.map((col) => {
    if (col === "ลำดับ") return "รวม";
    if (col === "ชื่อ-นามสกุล") return `${exportData.length} รายการ`;
    if (currencyNames.has(col))
      return exportData.reduce((s, r) => s + (Number(r[col]) || 0), 0);
    return "";
  });

  // ── Sheet 1: รายงาน ────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet([
    tableColumns,
    ...sheetRows,
    footerValues,
  ]);

  ws["!cols"] = tableColumns.map((col) => ({
    wch: Math.max(
      col.length + 2,
      ...exportData.map((r) => String(r[col] ?? "").length + 1),
    ),
  }));
  ws["!rows"] = [
    { hpx: 34 },
    ...Array(exportData.length).fill({ hpx: 22 }),
    { hpx: 28 },
  ];
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  const ROW_EVEN = "EBF5FB";
  const ROW_ODD = "FFFFFF";
  const ROW_OK = "D5F5E3";

  for (let C = 0; C < tableColumns.length; C++) {
    const col = tableColumns[C];
    const isCurrency = currencyNames.has(col);
    const isIndex = col === "ลำดับ";
    const isStatus = col === "สถานะ";
    const isAsset = col === "มีทรัพย์";
    const colLetter = XLSX.utils.encode_col(C);

    // ── Header ─────────────────────────────────────────────
    const hRef = `${colLetter}1`;
    if (!ws[hRef]) ws[hRef] = { t: "s", v: col };
    ws[hRef].s = XL_HEADER;

    // ── Data rows ───────────────────────────────────────────
    for (let R = 1; R <= exportData.length; R++) {
      const cellRef = `${colLetter}${R + 1}`;
      if (!ws[cellRef]) continue;
      const row = exportData[R - 1];
      const isSuccessRow = String(row?.["สถานะ"]) === "สำเร็จ";
      const evenRow = R % 2 === 0;
      const assetVal = String(row?.["มีทรัพย์"] ?? "");

      // font
      let fontColor = "1A1A1A";
      let fontBold = false;
      if (isSuccessRow) {
        fontColor = "1E8449";
        fontBold = true;
      }
      if (isAsset && !isSuccessRow) {
        fontColor = assetVal === "✓" ? "1E8449" : "C0392B";
        fontBold = true;
      }

      ws[cellRef].s = {
        font: {
          bold: fontBold,
          color: { rgb: fontColor },
          sz: isAsset ? 12 : 11,
        },
        fill: {
          patternType: "solid",
          fgColor: {
            rgb: isSuccessRow ? ROW_OK : evenRow ? ROW_EVEN : ROW_ODD,
          },
        },
        alignment: {
          horizontal: isCurrency
            ? "right"
            : isIndex || isStatus || isAsset
              ? "center"
              : "left",
          vertical: "center",
        },
        numFmt: isCurrency ? MONEY_FMT : isIndex ? INT_FMT : "@",
        border: BORDER_THIN(),
      };

      if (isIndex) {
        ws[cellRef].t = "n";
        ws[cellRef].v = Number(row[col]) || 0;
        ws[cellRef].z = INT_FMT;
      }
      if (isCurrency) {
        ws[cellRef].t = "n";
        ws[cellRef].v = Number(row[col]) || 0;
        ws[cellRef].z = MONEY_FMT;
      }
    }

    // ── Footer ──────────────────────────────────────────────
    const totalRows = exportData.length + 2;
    const fRef = `${colLetter}${totalRows}`;
    if (!ws[fRef]) ws[fRef] = {};
    const isLeft = col === "ลำดับ" || col === "ชื่อ-นามสกุล";
    ws[fRef].s = {
      ...XL_FOOTER,
      alignment: {
        horizontal: isLeft ? "left" : isCurrency ? "right" : "center",
        vertical: "center",
      },
    };
    if (isCurrency) {
      ws[fRef].t = "n";
      ws[fRef].v = footerValues[C];
      ws[fRef].z = MONEY_FMT;
    }
  }

  // ── Sheet 2: สรุปสถานะ ─────────────────────────────────────
  // หัวข้อ + ตาราง status
  const summaryAoa = [
    ["สถานะ", "จำนวน (คดี)"],
    ...statusCounts.map(([st, cnt]) => [st, cnt]),
    [],
    ["ข้อมูลรวม", ""],
    ["สำเร็จ (currentStatus ≥ 13)", successCount],
    ["รวมทั้งหมด", exportData.length],
    [],
    ["รวมค่าใช้จ่ายทั้งหมด (บาท)", totalExpenseAll],
    ["รวมคำพิพากษาทั้งหมด (บาท)", totalJudgmentAll],
    ["รวมลูกหนี้คงเหลือ (บาท)", totalBalanceAll],
    ["รวมส่วนต่างขาดทุน (บาท)", totalDiffAll],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryAoa);
  wsSummary["!cols"] = [{ wch: 38 }, { wch: 20 }];

  // style summary header row (row 1)
  ["A1", "B1"].forEach((ref) => {
    if (wsSummary[ref]) wsSummary[ref].s = XL_SUMMARY_HEADER;
  });

  // style "ข้อมูลรวม" sub-header
  const subHeaderRow = statusCounts.length + 3; // 1(header) + statusCount rows + 1(blank) + 1
  ["A", "B"].forEach((col) => {
    const ref = `${col}${subHeaderRow}`;
    if (wsSummary[ref]) wsSummary[ref].s = XL_SUMMARY_HEADER;
  });

  // style status data rows
  statusCounts.forEach((_, idx) => {
    const R = idx + 2; // row index (1-based), skip header
    const even = idx % 2 === 0;
    ["A", "B"].forEach((col, ci) => {
      const ref = `${col}${R}`;
      if (!wsSummary[ref]) return;
      wsSummary[ref].s = {
        font: { sz: 11, color: { rgb: "1A1A1A" } },
        fill: {
          patternType: "solid",
          fgColor: { rgb: even ? ROW_EVEN : ROW_ODD },
        },
        alignment: {
          horizontal: ci === 1 ? "right" : "left",
          vertical: "center",
        },
        numFmt: ci === 1 ? "0" : "@",
        border: BORDER_THIN(),
      };
      if (ci === 1) {
        wsSummary[ref].t = "n";
      }
    });
  });

  // style summary total rows (เขียว)
  const totalStartRow = statusCounts.length + 4;
  [
    totalStartRow,
    totalStartRow + 1,
    totalStartRow + 3,
    totalStartRow + 4,
  ].forEach((R) => {
    ["A", "B"].forEach((col, ci) => {
      const ref = `${col}${R}`;
      if (!wsSummary[ref]) return;
      wsSummary[ref].s = {
        font: {
          bold: true,
          sz: 11,
          color: { rgb: ci === 1 ? "1E5C8B" : "1A1A1A" },
        },
        fill: { patternType: "solid", fgColor: { rgb: "EBF5FB" } },
        alignment: {
          horizontal: ci === 1 ? "right" : "left",
          vertical: "center",
        },
        numFmt: ci === 1 ? (R >= totalStartRow + 3 ? MONEY_FMT : "0") : "@",
        border: BORDER_THIN(),
      };
      if (ci === 1 && R >= totalStartRow + 3) {
        wsSummary[ref].t = "n";
      }
    });
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
  XLSX.utils.book_append_sheet(wb, wsSummary, "สรุปสถานะ");
  if (hasPaymentList(sortedData)) {
    XLSX.utils.book_append_sheet(
      wb,
      buildPaymentSheet(sortedData),
      "รายการรับชำระ",
    );
  }
  XLSX.writeFile(wb, fileName);
};

export const exportToExcelMeeting = (
  data,
  getTaskStatus,
  fileName = "รายงานสรุปงานทนาย.xlsx",
) => {
  const sortedData = data; // เรียงมาแล้วจากหน้า dashboard (sortData)
  const exportData = prepareDataForExportMeeting(sortedData, getTaskStatus);
  const allExpenseKeys = collectAllExpenseKeys(sortedData);
  const currencyNames = getCurrencyColNames(allExpenseKeys);
  const statusCounts = buildStatusCount(exportData);
  const tableColumns = Object.keys(exportData[0] || {});

  // ── Summary values ─────────────────────────────────────────
  const totalJudgmentAll = sortedData.reduce(
    (s, i) => s + (parseFloat(i.totalJugde) || 0),
    0,
  );

  const totalExpenseAll = sortedData.reduce(
    (s, i) => s + getExpenseBreakdown(i).total,
    0,
  );
  const successCount = sortedData.filter(
    (i) => parseInt(i.currentStatus, 10) >= SUCCESS_STATUS_THRESHOLD,
  ).length;

  // ── Rows & footer ──────────────────────────────────────────
  const sheetRows = exportData.map((row) =>
    tableColumns.map((col) => row[col]),
  );
  const footerValues = tableColumns.map((col) => {
    if (col === "ลำดับ") return "รวม";
    if (col === "ชื่อ-นามสกุล") return `${exportData.length} รายการ`;
    if (currencyNames.has(col))
      return exportData.reduce((s, r) => s + (Number(r[col]) || 0), 0);
    return "";
  });

  // ── Sheet 1: รายงาน ────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet([
    tableColumns,
    ...sheetRows,
    footerValues,
  ]);

  ws["!cols"] = tableColumns.map((col) => ({
    wch: Math.max(
      col.length + 2,
      ...exportData.map((r) => String(r[col] ?? "").length + 1),
    ),
  }));
  ws["!rows"] = [
    { hpx: 34 },
    ...Array(exportData.length).fill({ hpx: 22 }),
    { hpx: 28 },
  ];
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  const ROW_EVEN = "EBF5FB";
  const ROW_ODD = "FFFFFF";
  const ROW_OK = "D5F5E3";

  for (let C = 0; C < tableColumns.length; C++) {
    const col = tableColumns[C];
    const isCurrency = currencyNames.has(col);
    const isIndex = col === "ลำดับ";
    const isStatus = col === "สถานะ";
    const isAsset = col === "มีทรัพย์";
    const colLetter = XLSX.utils.encode_col(C);

    // ── Header ─────────────────────────────────────────────
    const hRef = `${colLetter}1`;
    if (!ws[hRef]) ws[hRef] = { t: "s", v: col };
    ws[hRef].s = XL_HEADER;

    // ── Data rows ───────────────────────────────────────────
    for (let R = 1; R <= exportData.length; R++) {
      const cellRef = `${colLetter}${R + 1}`;
      if (!ws[cellRef]) continue;
      const row = exportData[R - 1];
      const isSuccessRow = String(row?.["สถานะ"]) === "สำเร็จ";
      const evenRow = R % 2 === 0;
      const assetVal = String(row?.["มีทรัพย์"] ?? "");

      // font
      let fontColor = "1A1A1A";
      let fontBold = false;
      if (isSuccessRow) {
        fontColor = "1E8449";
        fontBold = true;
      }
      if (isAsset && !isSuccessRow) {
        fontColor = assetVal === "✓" ? "1E8449" : "C0392B";
        fontBold = true;
      }

      ws[cellRef].s = {
        font: {
          bold: fontBold,
          color: { rgb: fontColor },
          sz: isAsset ? 12 : 11,
        },
        fill: {
          patternType: "solid",
          fgColor: {
            rgb: isSuccessRow ? ROW_OK : evenRow ? ROW_EVEN : ROW_ODD,
          },
        },
        alignment: {
          horizontal: isCurrency
            ? "right"
            : isIndex || isStatus || isAsset
              ? "center"
              : "left",
          vertical: "center",
        },
        numFmt: isCurrency ? MONEY_FMT : isIndex ? INT_FMT : "@",
        border: BORDER_THIN(),
      };

      if (isIndex) {
        ws[cellRef].t = "n";
        ws[cellRef].v = Number(row[col]) || 0;
        ws[cellRef].z = INT_FMT;
      }
      if (isCurrency) {
        ws[cellRef].t = "n";
        ws[cellRef].v = Number(row[col]) || 0;
        ws[cellRef].z = MONEY_FMT;
      }
    }

    // ── Footer ──────────────────────────────────────────────
    const totalRows = exportData.length + 2;
    const fRef = `${colLetter}${totalRows}`;
    if (!ws[fRef]) ws[fRef] = {};
    const isLeft = col === "ลำดับ" || col === "ชื่อ-นามสกุล";
    ws[fRef].s = {
      ...XL_FOOTER,
      alignment: {
        horizontal: isLeft ? "left" : isCurrency ? "right" : "center",
        vertical: "center",
      },
    };
    if (isCurrency) {
      ws[fRef].t = "n";
      ws[fRef].v = footerValues[C];
      ws[fRef].z = MONEY_FMT;
    }
  }

  // ── Sheet 2: สรุปสถานะ ─────────────────────────────────────
  // หัวข้อ + ตาราง status
  const summaryAoa = [
    ["สถานะ", "จำนวน (คดี)"],
    ...statusCounts.map(([st, cnt]) => [st, cnt]),
    [],
    ["ข้อมูลรวม", ""],
    ["สำเร็จ (currentStatus ≥ 13)", successCount],
    ["รวมทั้งหมด", exportData.length],
    [],
    ["รวมคำพิพากษาทั้งหมด (บาท)", totalJudgmentAll],
    ["รวมค่าใช้จ่ายทั้งหมด (บาท)", totalExpenseAll],
  ];

  const wsSummary = XLSX.utils.aoa_to_sheet(summaryAoa);
  wsSummary["!cols"] = [{ wch: 38 }, { wch: 20 }];

  // style summary header row (row 1)
  ["A1", "B1"].forEach((ref) => {
    if (wsSummary[ref]) wsSummary[ref].s = XL_SUMMARY_HEADER;
  });

  // style "ข้อมูลรวม" sub-header
  const subHeaderRow = statusCounts.length + 3; // 1(header) + statusCount rows + 1(blank) + 1
  ["A", "B"].forEach((col) => {
    const ref = `${col}${subHeaderRow}`;
    if (wsSummary[ref]) wsSummary[ref].s = XL_SUMMARY_HEADER;
  });

  // style status data rows
  statusCounts.forEach((_, idx) => {
    const R = idx + 2; // row index (1-based), skip header
    const even = idx % 2 === 0;
    ["A", "B"].forEach((col, ci) => {
      const ref = `${col}${R}`;
      if (!wsSummary[ref]) return;
      wsSummary[ref].s = {
        font: { sz: 11, color: { rgb: "1A1A1A" } },
        fill: {
          patternType: "solid",
          fgColor: { rgb: even ? ROW_EVEN : ROW_ODD },
        },
        alignment: {
          horizontal: ci === 1 ? "right" : "left",
          vertical: "center",
        },
        numFmt: ci === 1 ? "0" : "@",
        border: BORDER_THIN(),
      };
      if (ci === 1) {
        wsSummary[ref].t = "n";
      }
    });
  });

  // style summary total rows (เขียว)
  const totalStartRow = statusCounts.length + 4;
  [
    totalStartRow,
    totalStartRow + 1,
    totalStartRow + 3,
    totalStartRow + 4,
  ].forEach((R) => {
    ["A", "B"].forEach((col, ci) => {
      const ref = `${col}${R}`;
      if (!wsSummary[ref]) return;
      wsSummary[ref].s = {
        font: {
          bold: true,
          sz: 11,
          color: { rgb: ci === 1 ? "1E5C8B" : "1A1A1A" },
        },
        fill: { patternType: "solid", fgColor: { rgb: "EBF5FB" } },
        alignment: {
          horizontal: ci === 1 ? "right" : "left",
          vertical: "center",
        },
        numFmt: ci === 1 ? (R >= totalStartRow + 3 ? MONEY_FMT : "0") : "@",
        border: BORDER_THIN(),
      };
      if (ci === 1 && R >= totalStartRow + 3) {
        wsSummary[ref].t = "n";
      }
    });
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
  XLSX.utils.book_append_sheet(wb, wsSummary, "สรุปสถานะ");
  XLSX.writeFile(wb, fileName);
};

// ════════════════════════════════════════════════════════════
//  Export PDF
// ════════════════════════════════════════════════════════════

export const exportToPDF = (
  data,
  getTaskStatus,
  lawyerName = "",
  fileName = "รายงานสรุปงานทนาย.pdf",
) => {
  const sortedData = data; // เรียงมาแล้วจากหน้า dashboard (sortData)
  const exportData = prepareDataForExport(sortedData, getTaskStatus);
  const allExpenseKeys = collectAllExpenseKeys(sortedData);

  // ✅ PDF ไม่ใช้ 2 คอลัมน์นี้
  const PDF_EXCLUDE = new Set(["ลูกหนี้คงเหลือ (บาท)", "ส่วนต่างขาดทุน (บาท)"]);
  const tableColumns = Object.keys(exportData[0] || {}).filter(
    (col) => !PDF_EXCLUDE.has(col),
  );

  const tableRows = exportData.map((row) =>
    tableColumns.map((col) => {
      const val = row[col];
      if (col === "ลำดับ") return String(val);
      if (typeof val === "number" && val > 0)
        return val.toLocaleString("th-TH", { minimumFractionDigits: 2 });
      if (typeof val === "number" && val === 0) return "-";
      return val ?? "-";
    }),
  );

  const currencyNames = getCurrencyColNames(allExpenseKeys);
  const currencyColIdxSet = new Set(
    tableColumns
      .map((col, i) => (currencyNames.has(col) ? i : -1))
      .filter((i) => i >= 0),
  );
  const statusColIdx = tableColumns.indexOf("สถานะ");
  const assetColIdx = tableColumns.indexOf("มีทรัพย์");
  const statusCounts = buildStatusCount(exportData);

  const totalJudgmentAll = sortedData.reduce(
    (s, i) => s + (parseFloat(i.totalJugde) || 0),
    0,
  );
  const totalExpenseAll = sortedData.reduce(
    (s, i) => s + getExpenseBreakdown(i).total,
    0,
  );
  const successCount = sortedData.filter(
    (i) => parseInt(i.currentStatus, 10) >= SUCCESS_STATUS_THRESHOLD,
  ).length;

  // ── Footer row ─────────────────────────────────────────────
  const footerRow = tableColumns.map((col) => {
    if (col === "ลำดับ")
      return {
        content: "รวม",
        styles: {
          fontStyle: "bold",
          halign: "center",
          textColor: [255, 255, 255],
        },
      };
    if (col === "ชื่อ-นามสกุล")
      return {
        content: `${exportData.length} รายการ`,
        styles: {
          fontStyle: "bold",
          halign: "left",
          textColor: [255, 255, 255],
        },
      };
    if (currencyNames.has(col)) {
      const total = exportData.reduce((s, r) => s + (Number(r[col]) || 0), 0);
      return {
        content:
          total > 0
            ? total.toLocaleString("th-TH", { minimumFractionDigits: 2 })
            : "-",
        styles: {
          fontStyle: "bold",
          halign: "right",
          textColor: [255, 255, 255],
        },
      };
    }
    return { content: "", styles: { textColor: [255, 255, 255] } };
  });

  // ── columnStyles + scale to full page ─────────────────────
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 4;
  const USABLE_W = PAGE_W - MARGIN * 2;

  const BASE_WIDTHS = [14, 24, 26, 40, 20, 20, 20, 22, 25, 20, 15, 25, 25];
  const expW = allExpenseKeys.map(() => 20);
  const allWidths = [...BASE_WIDTHS, ...expW, 22];
  const baseTotal = allWidths.reduce((s, w) => s + w, 0);
  const scale = USABLE_W / baseTotal;

  const columnStyles = {};
  const HALIGNS = [
    "center",
    "center",
    "center",
    "left",
    "center",
    "center",
    "center",
    "center",
    "center",
    "center",
    "center",
    "right",
    "right",
  ];
  allWidths.forEach((w, i) => {
    columnStyles[i] = {
      cellWidth: Math.floor(w * scale),
      halign: i < HALIGNS.length ? HALIGNS[i] : "right",
    };
  });

  // ── สี ─────────────────────────────────────────────────────
  const ROW_ODD_PDF = [255, 255, 255];
  const ROW_EVEN_PDF = [235, 245, 251];
  const ROW_OK_PDF = [213, 245, 227];
  const FONT_OK_PDF = [30, 132, 73];
  const FONT_YES_PDF = [30, 132, 73];
  const FONT_NO_PDF = [192, 57, 43];

  const SUMMARY_COLS = 3;
  const SUMMARY_COLW = 90;
  const SUMMARY_LINEH = 6.5;
  const summaryH =
    14 + Math.ceil(statusCounts.length / SUMMARY_COLS) * SUMMARY_LINEH + 14;

  // ── Header text ────────────────────────────────────────────
  doc.setFont("THSarabunNew", "bold");
  doc.setFontSize(22);
  doc.text("รายงานสรุปงานทนาย", MARGIN + 10, 16);

  doc.setFont("THSarabunNew", "normal");
  doc.setFontSize(14);
  doc.text(
    `วันที่ออกรายงาน: ${new Date().toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })}${lawyerName ? `  |  ทนาย: ${lawyerName}` : ""}`,
    MARGIN + 10,
    24,
  );
  doc.text(`จำนวนทั้งหมด: ${exportData.length} รายการ`, MARGIN + 10, 30);

  doc.setFontSize(13);
  doc.text(
    `สำเร็จ: ${successCount} คดี  |  รวมคำพิพากษา: ${fmt(totalJudgmentAll)} บาท  |  รวมค่าใช้จ่าย: ${fmt(totalExpenseAll)} บาท`,
    MARGIN + 10,
    36,
  );

  doc.autoTable({
    head: [tableColumns],
    body: tableRows,
    foot: [footerRow],
    startY: 42,
    margin: { top: 42, left: MARGIN, right: MARGIN },
    tableWidth: USABLE_W,
    styles: {
      font: "THSarabunNew",
      fontSize: 9.5,
      cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
      overflow: "linebreak",
      lineColor: [210, 210, 210],
      lineWidth: 0.1,
      textColor: [30, 30, 30],
    },
    headStyles: {
      font: "THSarabunNew",
      fillColor: [31, 97, 209],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      fontSize: 10,
      cellPadding: { top: 3.5, right: 2.5, bottom: 3.5, left: 2.5 },
    },
    footStyles: {
      font: "THSarabunNew",
      fillColor: [44, 62, 80],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 3, right: 2.5, bottom: 3, left: 2.5 },
    },
    alternateRowStyles: { fillColor: ROW_EVEN_PDF },
    rowPageBreak: "avoid",
    columnStyles,
    showFoot: "lastPage",
    didParseCell(hook) {
      if (hook.section !== "body") return;
      const isSuccessRow = String(hook.row.raw[statusColIdx]) === "สำเร็จ";
      hook.cell.styles.fillColor = isSuccessRow
        ? ROW_OK_PDF
        : hook.row.index % 2 === 0
          ? ROW_ODD_PDF
          : ROW_EVEN_PDF;
      if (isSuccessRow) {
        hook.cell.styles.textColor = FONT_OK_PDF;
        hook.cell.styles.fontStyle = "bold";
      }
      if (hook.column.index === assetColIdx) {
        const v = String(hook.cell.raw ?? "");
        hook.cell.styles.fontStyle = "bold";
        hook.cell.styles.textColor = v === "✓" ? FONT_YES_PDF : FONT_NO_PDF;
        hook.cell.styles.halign = "center";
        hook.cell.styles.fontSize = 11;
      }
      if (currencyColIdxSet.has(hook.column.index))
        hook.cell.styles.halign = "right";
    },
  });

  // ── สรุปสถานะ ──────────────────────────────────────────────
  const pageH = doc.internal.pageSize.getHeight();
  const finalY = doc.lastAutoTable?.finalY ?? pageH - 50;
  const spaceLeft = pageH - finalY - 12;

  let summaryY;
  if (spaceLeft < summaryH + 5) {
    doc.addPage();
    summaryY = 20;
  } else {
    summaryY = finalY + 10;
  }

  doc.setFont("THSarabunNew", "bold");
  doc.setFontSize(14);
  doc.text("สรุปสถานะ:", MARGIN + 10, summaryY);

  doc.setFont("THSarabunNew", "normal");
  doc.setFontSize(11);
  statusCounts.forEach(([st, cnt], idx) => {
    const cx = MARGIN + 10 + (idx % SUMMARY_COLS) * SUMMARY_COLW;
    const cy = summaryY + 8 + Math.floor(idx / SUMMARY_COLS) * SUMMARY_LINEH;
    doc.text(`• ${st}: ${cnt} คดี`, cx, cy);
  });

  // เลขหน้า
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont("THSarabunNew", "normal");
    doc.setFontSize(10);
    doc.text(
      `หน้า ${p} / ${totalPages}`,
      PAGE_W - MARGIN - 4,
      doc.internal.pageSize.getHeight() - 5,
      { align: "right" },
    );
  }

  doc.save(fileName);
};

export const exportToPDFMeeting = (
  data,
  getTaskStatus,
  lawyerName = "",
  fileName = "รายงานสรุปงานทนาย.pdf",
) => {
  const sortedData = data; // เรียงมาแล้วจากหน้า dashboard (sortData)
  const exportData = prepareDataForExportMeeting(sortedData, getTaskStatus);

  const tableColumns = Object.keys(exportData[0] || {});

  const tableRows = exportData.map((row) =>
    tableColumns.map((col) => {
      const val = row[col];
      return val ?? "-";
    }),
  );

  const statusColIdx = tableColumns.indexOf("สถานะ");
  const assetColIdx = tableColumns.indexOf("มีทรัพย์");
  const statusCounts = buildStatusCount(exportData);

  const totalJudgmentAll = sortedData.reduce(
    (s, i) => s + (parseFloat(i.totalJugde) || 0),
    0,
  );
  const totalExpenseAll = sortedData.reduce(
    (s, i) => s + getExpenseBreakdown(i).total,
    0,
  );
  const successCount = sortedData.filter(
    (i) => parseInt(i.currentStatus, 10) >= SUCCESS_STATUS_THRESHOLD,
  ).length;

  // ── Footer row ─────────────────────────────────────────────
  const footerRow = tableColumns.map((col) => {
    if (col === "ลำดับ")
      return {
        content: "รวม",
        styles: {
          fontStyle: "bold",
          halign: "center",
          textColor: [255, 255, 255],
        },
      };
    if (col === "ชื่อ-นามสกุล")
      return {
        content: `${exportData.length} รายการ`,
        styles: {
          fontStyle: "bold",
          halign: "left",
          textColor: [255, 255, 255],
        },
      };
    return { content: "", styles: { textColor: [255, 255, 255] } };
  });

  // ── columnStyles + scale to full page ─────────────────────
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3" });
  const PAGE_W = doc.internal.pageSize.getWidth();
  const MARGIN = 4;
  const USABLE_W = PAGE_W - MARGIN * 2;

  const BASE_WIDTHS = [14, 24, 40, 22, 24, 20, 30, 20];
  const baseTotal = BASE_WIDTHS.reduce((s, w) => s + w, 0);
  const scale = USABLE_W / baseTotal;

  const columnStyles = {};
  const HALIGNS = [
    "center",
    "center",
    "left",
    "center",
    "center",
    "center",
    "center",
    "center",
  ];
  BASE_WIDTHS.forEach((w, i) => {
    columnStyles[i] = {
      cellWidth: Math.floor(w * scale),
      halign: i < HALIGNS.length ? HALIGNS[i] : "center",
    };
  });

  // ── สี ─────────────────────────────────────────────────────
  const ROW_ODD_PDF = [255, 255, 255];
  const ROW_EVEN_PDF = [235, 245, 251];
  const ROW_OK_PDF = [213, 245, 227];
  const FONT_OK_PDF = [30, 132, 73];
  const FONT_YES_PDF = [30, 132, 73];
  const FONT_NO_PDF = [192, 57, 43];

  const SUMMARY_COLS = 3;
  const SUMMARY_COLW = 90;
  const SUMMARY_LINEH = 6.5;
  const summaryH =
    14 + Math.ceil(statusCounts.length / SUMMARY_COLS) * SUMMARY_LINEH + 14;

  // ── Header text ────────────────────────────────────────────
  doc.setFont("THSarabunNew", "bold");
  doc.setFontSize(22);
  doc.text("รายงานสรุปงานทนาย (นัดประชุม)", MARGIN + 10, 16);

  doc.setFont("THSarabunNew", "normal");
  doc.setFontSize(14);
  doc.text(
    `วันที่ออกรายงาน: ${new Date().toLocaleDateString("th-TH", { year: "numeric", month: "long", day: "numeric" })}${lawyerName ? `  |  ทนาย: ${lawyerName}` : ""}`,
    MARGIN + 10,
    24,
  );
  doc.text(`จำนวนทั้งหมด: ${exportData.length} รายการ`, MARGIN + 10, 30);

  doc.setFontSize(13);
  doc.text(
    `สำเร็จ: ${successCount} คดี  |  รวมคำพิพากษา: ${fmt(totalJudgmentAll)} บาท  |  รวมค่าใช้จ่าย: ${fmt(totalExpenseAll)} บาท`,
    MARGIN + 10,
    36,
  );

  doc.autoTable({
    head: [tableColumns],
    body: tableRows,
    foot: [footerRow],
    startY: 42,
    margin: { top: 42, left: MARGIN, right: MARGIN },
    tableWidth: USABLE_W,
    styles: {
      font: "THSarabunNew",
      fontSize: 9.5,
      cellPadding: { top: 2.5, right: 2.5, bottom: 2.5, left: 2.5 },
      overflow: "linebreak",
      lineColor: [210, 210, 210],
      lineWidth: 0.1,
      textColor: [30, 30, 30],
    },
    headStyles: {
      font: "THSarabunNew",
      fillColor: [31, 97, 209],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      fontSize: 10,
      cellPadding: { top: 3.5, right: 2.5, bottom: 3.5, left: 2.5 },
    },
    footStyles: {
      font: "THSarabunNew",
      fillColor: [44, 62, 80],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 9.5,
      cellPadding: { top: 3, right: 2.5, bottom: 3, left: 2.5 },
    },
    alternateRowStyles: { fillColor: ROW_EVEN_PDF },
    rowPageBreak: "avoid",
    columnStyles,
    showFoot: "lastPage",
    didParseCell(hook) {
      if (hook.section !== "body") return;
      const isSuccessRow = String(hook.row.raw[statusColIdx]) === "สำเร็จ";
      hook.cell.styles.fillColor = isSuccessRow
        ? ROW_OK_PDF
        : hook.row.index % 2 === 0
          ? ROW_ODD_PDF
          : ROW_EVEN_PDF;
      if (isSuccessRow) {
        hook.cell.styles.textColor = FONT_OK_PDF;
        hook.cell.styles.fontStyle = "bold";
      }
      if (hook.column.index === assetColIdx) {
        const v = String(hook.cell.raw ?? "");
        hook.cell.styles.fontStyle = "bold";
        hook.cell.styles.textColor =
          v === "มี"
            ? FONT_YES_PDF
            : v === "ไม่มี"
              ? FONT_NO_PDF
              : hook.cell.styles.textColor;
        hook.cell.styles.halign = "center";
        hook.cell.styles.fontSize = 11;
      }
    },
  });

  // ── สรุปสถานะ ──────────────────────────────────────────────
  const pageH = doc.internal.pageSize.getHeight();
  const finalY = doc.lastAutoTable?.finalY ?? pageH - 50;
  const spaceLeft = pageH - finalY - 12;

  let summaryY;
  if (spaceLeft < summaryH + 5) {
    doc.addPage();
    summaryY = 20;
  } else {
    summaryY = finalY + 10;
  }

  doc.setFont("THSarabunNew", "bold");
  doc.setFontSize(14);
  doc.text("สรุปสถานะ:", MARGIN + 10, summaryY);

  doc.setFont("THSarabunNew", "normal");
  doc.setFontSize(11);
  statusCounts.forEach(([st, cnt], idx) => {
    const cx = MARGIN + 10 + (idx % SUMMARY_COLS) * SUMMARY_COLW;
    const cy = summaryY + 8 + Math.floor(idx / SUMMARY_COLS) * SUMMARY_LINEH;
    doc.text(`• ${st}: ${cnt} คดี`, cx, cy);
  });

  // เลขหน้า
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont("THSarabunNew", "normal");
    doc.setFontSize(10);
    doc.text(
      `หน้า ${p} / ${totalPages}`,
      PAGE_W - MARGIN - 4,
      doc.internal.pageSize.getHeight() - 5,
      { align: "right" },
    );
  }

  doc.save(fileName);
};
